export type LockArgs = {
  organizationId: string;
  deviceId: string;
  serialNumber: string;
  lockMethod?: string;
};

export type WipeArgs = {
  organizationId: string;
  deviceId: string;
  wipeMethod?: string;
};

export type DeleteSystemBySerialNumberArgs = {
  organizationId: string;
  serialNumber: string;
};

export enum ProvisionStatusEnum {
  PROVISIONING_STATUS = 'provisioning',
  PENDING_CERTIFICATE_STATUS = 'pending_certificate',
  COMPLETED_STATUS = 'completed',
}

export enum AppleDepServerTokenStateEnum {
  VALID = 'valid',
  MISSING = 'missing',
}

export type JumpCloudAppleMdmProvisionArgs = {
  organizationId: string;
  appleCertCreatorAppleId: string;
  appleSignedCert: string;
  dep?: object;
  encryptedDepServerToken?: string;
  allowMobileUserEnrollment?: boolean;
  defaultIosUserEnrollmentDeviceGroupId?: string;
  defaultSystemGroupId?: string;
  name?: string;
};

export type AppleCertificate = {
  creatorAppleId: string;
  serialNumber: string;
  // String because our redux store is not able to handle Date objects
  expiresAt: string;
};

export type AppleDepInfo = {
  depServerTokenState: string;
  expiresAt: string;
};

export type MDMInformation = {
  mdmOrganizationId?: string;
  appleCertificate?: AppleCertificate;
  appleMdmDepInfo?: AppleDepInfo;
};

export type UnenrollDeviceArgs = {
  organizationId: string;
  deviceId: string;
};

export type GetSystemRecoveryKeyArgs = {
  organizationId: string;
  serialNumber: string;
};

export type SystemRecoveryKeyResponse = {
  recoveryKey: string;
};

export type OsVersionDetail = {
  distributionName?: string;
  major?: string;
  majorNumber?: number;
  minor?: string;
  minorNumber?: number;
  osName?: string;
  patch?: string;
  patchNumber?: number;
  releaseName?: string;
  revision?: string;
  version?: string;
};

export type SystemMDM = {
  dep?: boolean;
  enrollmentType?: string;
  internal?: object;
  profileIdentifier?: string;
  providerId?: string;
  userApproved?: boolean;
  vendor?: string;
};

export type System = {
  id?: string;
  active?: boolean;
  agentVersion?: string;
  allowMultiFactorAuth?: boolean;
  allowPublicKeyAuth?: boolean;
  allowSshPasswordAuth?: boolean;
  allowSshRootLogin?: boolean;
  arch?: string;
  azureAdJoined?: boolean;
  connectionHistory?: Array<object>;
  created?: string;
  description?: string;
  displayName?: string;
  domainInfo?: object;
  fde?: object;
  fileSystem?: string;
  hasServiceAccount?: boolean;
  hostname?: string;
  lastContact?: string;
  mdm?: SystemMDM;
  modifySshdConfig?: boolean;
  networkInterfaces?: Array<object>;
  organization?: string;
  os?: string;
  osFamily?: string;
  osVersionDetail?: OsVersionDetail;
  remoteIp?: string;
  serialNumber?: string;
  serviceAccountState?: object;
  sshRootEnabled?: boolean;
  sshdParams?: Array<object>;
  systemInsights?: object;
  systemTimezone?: number;
  templateName?: string;
  usernameHashes?: object;
  userMetrics?: Array<object>;
  version?: string;
};

export type PolicyCountData = {
  total?: number;
  success?: number;
  failed?: number;
  pending?: number;
  duplicate?: number;
  unsupportedOs?: number;
};

export type PolicyStats = {
  failedPolicies?: Array<string>;
  pendingPolicies?: Array<string>;
  policyCountData?: PolicyCountData;
};

export type SystemWithPolicyStats = {
  system: System;
  policyStats?: PolicyStats;
};

// ─── Software Catalog Types ───────────────────────────────────────────────────

/** OS type values as used by the JumpCloud API */
export type SoftwareOsType = 'darwin' | 'windows';

/** A single app entry returned from GET /software-catalog/apps */
export type SoftwareCatalogApp = {
  software_app_id: string;
  app_name: string;
  os_type: SoftwareOsType;
  description?: string;
  icon_url?: string;
  /** False when the org has already added this app for the same OS. */
  is_available: boolean;
};

/** A single app entry returned from GET /software-apps */
export type OrgSoftwareApp = {
  software_app_id: string;
  app_name: string;
  os_type: SoftwareOsType;
  description?: string;
  icon_url?: string;
};

/** Args for GET /software-catalog/apps */
export type GetSoftwareCatalogArgs = {
  organizationId: string;
  osType: SoftwareOsType;
  query?: string;
  limit?: number;
  skip?: number;
};

/** Args for GET /software-apps */
export type GetOrgSoftwareAppsArgs = {
  organizationId: string;
};

/** Args for POST /software-apps — only the catalog app ID and OS type are needed. */
export type AddSoftwareAppArgs = {
  organizationId: string;
  softwareAppId: string;
  osType: SoftwareOsType;
};

/** Args for DELETE /software-apps/{software_app_id} */
export type RemoveSoftwareAppArgs = {
  organizationId: string;
  softwareAppId: string;
};
